import React, { useCallback, useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { ScreenSpinner, Tabs, TabsItem } from "@vkontakte/vkui";
import Div from "@vkontakte/vkui/dist/components/Div/Div";
import Button from "@vkontakte/vkui/dist/components/Button/Button";
import MaskedInput from "react-text-mask";
import { bindActionCreators } from "redux";
import Text from "@vkontakte/vkui/dist/components/Typography/Text/Text";
import { Formik } from "formik";
import { userAuthenticationStarted } from "../../../redux/actions/userActions";
import stylesFn from "./LoginForm.style";
import { userInfoLoadingStarted } from "../../../redux/actions/userInfoActions";
import { hideLoginFormLoader } from "../../../redux/actions/loaderActions";
import { OAuthGosuslugi } from "../../../services/vk/redux";
import { setMetrics } from "../../../helpers/metrics";

const snilsMask = () => {
  return [
    /\d/,
    /\d/,
    /\d/,
    "-",
    /\d/,
    /\d/,
    /\d/,
    "-",
    /\d/,
    /\d/,
    /\d/,
    " ",
    /\d/,
    /\d/,
  ];
};

const phoneRegExp = /[78]?[- _]*\(?[- _]*(\d{3}[- _]*\)?([- _]*\d){7}|\d\d[- _]*\d\d[- _]*\)?([- _]*\d){6}$)/;
// eslint-disable-next-line no-useless-escape
const emailRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const snilsRegExp = /^[0-9]{3}-[0-9]{3}-[0-9]{3}\s[0-9]{2}$/;

/**
 * Returns login type (PHONE, EMAIL or SNILS)
 * @param username
 * @param selectedTab - phone-email or snils
 * @returns {string} - PHONE, EMAIL or SNILS
 */
const getLoginType = (username, selectedTab) => {
  if (selectedTab === "phone-email") {
    if (username.match(phoneRegExp)) {
      return "PHONE";
    }
    if (username.match(emailRegExp)) {
      return "EMAIL";
    }
  }
  return "SNILS";
};

const getLoginFormTabsStyles = (loginFormIsSubmitting, styles) => {
  if (loginFormIsSubmitting) {
    return {
      display: "none",
    };
  }

  return styles.loginFormTabs;
};

const getLoginFormStyles = (loginFormIsSubmitting, styles) => {
  if (loginFormIsSubmitting) {
    return {
      display: "none",
    };
  }

  return styles.loginForm;
};

const getInputStyles = (inputHasFocus, styles) => {
  if (inputHasFocus) {
    return styles.inputLabel;
  }

  return {
    visibility: "hidden",
  };
};

const LoginForm = ({
  loginFormIsSubmitting,
  startAuthentication,
  startUserInfoLoading,
  dispatchHideLoginFormLoader,
}) => {
  // "snils" or "phone-email"
  const [selectedTab, setSelectedTab] = useState("snils");
  const dispatch = useDispatch();
  // "password" or "text"
  const [passwordType, setPasswordType] = useState("password");
  const isDev = useSelector((state) => state.addresses?.isDev);

  const [snilsInputHasFocus, setSnilsInputHasFocus] = useState(false);
  const [phoneOrEmailInputHasFocus, setPhoneOrEmailInputHasFocus] = useState(
    false
  );
  const [passwordInputHasFocus, setPasswordInputHasFocus] = useState(false);

  const orientationLandscape = useMediaQuery({ minAspectRatio: "13/9" });

  const styles = stylesFn(orientationLandscape);

  const togglePasswordType = useCallback(() => {
    setPasswordType((p) => {
      if (p === "password") {
        return "text";
      }
      return "password";
    });
  }, []);

  const validate = useCallback(
    (values) => {
      const errors = {};

      if (selectedTab === "snils") {
        if (!values.snils) {
          errors.snils = "Поле обязательно для заполнения";
        } else if (!values.snils.match(snilsRegExp)) {
          errors.snils = "Введите СНИЛС";
        }
      } else if (!values.phoneOrEmail) {
        errors.phoneOrEmail = "Поле обязательно для заполнения";
      } else if (
        !values.phoneOrEmail.match(phoneRegExp) &&
        !values.phoneOrEmail.match(emailRegExp)
      ) {
        errors.phoneOrEmail = "Введите телефон или почту";
      }

      if (!values.password) {
        errors.password = "Поле обязательно для заполнения";
      }

      return {};
    },
    [selectedTab]
  );

  const submitForm = useCallback(
    (values, formikHelpers) => {
      const username =
        selectedTab === "snils" ? values.snils : values.phoneOrEmail;

      const { password } = values;

      // я также собирался здесь вещать action showLoginFormLoader(), но в функции showLoginFormLoader
      // зачем-то сбрасывается стейт, поэтому приходится вызывать showLoginFormLoader() в showLoginFormLoader
      // после сброса стейта, а здесь в конце цепочки промисов вызывать hideLoginFormLoader()

      return startAuthentication(
        username,
        password,
        getLoginType(username, selectedTab)
      )
        .then(() => {
          return startUserInfoLoading();
        })
        .catch((err) => {
          formikHelpers.setStatus(err);
        })
        .finally(() => dispatchHideLoginFormLoader());
    },
    [
      dispatchHideLoginFormLoader,
      selectedTab,
      startAuthentication,
      startUserInfoLoading,
    ]
  );

  const handleOAuthGosuslugi = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(OAuthGosuslugi());
    },
    [dispatch]
  );

  useEffect(() => setMetrics("reachGoal", "OldHealth.Authorization"), []);

  return (
    // eslint-disable-next-line react/jsx-filename-extension
    <>
      {loginFormIsSubmitting && (
        <Div style={styles.spinner}>
          <ScreenSpinner />
        </Div>
      )}
      {isDev && (
        <Tabs style={getLoginFormTabsStyles(loginFormIsSubmitting, styles)}>
          <TabsItem
            style={
              selectedTab === "phone-email"
                ? styles.activeTab
                : styles.inactiveTab
            }
            onClick={() => setSelectedTab("phone-email")}
            selected={selectedTab === "phone-email"}
          >
            Телефон или почта
          </TabsItem>
          <TabsItem
            style={
              selectedTab === "snils" ? styles.activeTab : styles.inactiveTab
            }
            onClick={() => setSelectedTab("snils")}
            selected={selectedTab === "snils"}
          >
            СНИЛС
          </TabsItem>
        </Tabs>
      )}
      <Div style={getLoginFormStyles(loginFormIsSubmitting, styles)}>
        <Formik
          initialValues={{
            snils: "1078941437",
            phoneOrEmail: "",
            password: "password",
          }}
          validate={validate}
          onSubmit={submitForm}
        >
          {({
            errors,
            values,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            status,
            setStatus,
            /* and other goodies */
          }) => (
            <form
              noValidate
              onSubmit={(e) => handleSubmit(e)}
              onChange={() => setStatus("")}
            >
              {!!status && <Text style={styles.errorMessage}>{status}</Text>}

              {isDev && (
                <>
                  {selectedTab === "snils" ? (
                    <Div style={styles.usernameInputWrapper}>
                      <MaskedInput
                        type="tel"
                        mask={snilsMask}
                        onChange={handleChange}
                        onFocus={() => {
                          setSnilsInputHasFocus(true);
                        }}
                        onBlur={(e) => {
                          setSnilsInputHasFocus(false);
                          handleBlur(e);
                        }}
                        style={styles.userNameInput}
                        placeholder={snilsInputHasFocus ? "" : "СНИЛС"}
                        name="snils"
                        value={values.snils}
                      />
                      {errors.snils && touched.snils && (
                        <Text weight="regular" style={styles.fieldErrorMessage}>
                          {errors.snils}
                        </Text>
                      )}
                      <Text
                        weight="regular"
                        style={getInputStyles(snilsInputHasFocus, styles)}
                      >
                        СНИЛС
                      </Text>
                    </Div>
                  ) : (
                    <Div style={styles.usernameInputWrapper}>
                      <input
                        type="text"
                        onChange={(e) => {
                          handleChange(e);
                        }}
                        onFocus={() => {
                          setPhoneOrEmailInputHasFocus(true);
                        }}
                        onBlur={(e) => {
                          setPhoneOrEmailInputHasFocus(false);
                          handleBlur(e);
                        }}
                        style={styles.phonePostInput}
                        placeholder={
                          phoneOrEmailInputHasFocus ? "" : "Телефон или почта"
                        }
                        name="phoneOrEmail"
                        value={values.phoneOrEmail}
                      />
                      {errors.phoneOrEmail && touched.phoneOrEmail && (
                        <Text weight="regular" style={styles.fieldErrorMessage}>
                          {errors.phoneOrEmail}
                        </Text>
                      )}
                      <Text
                        weight="regular"
                        style={getInputStyles(
                          phoneOrEmailInputHasFocus,
                          styles
                        )}
                      >
                        Мобильный телефон или почта
                      </Text>
                    </Div>
                  )}

                  <Div style={styles.passwordInputWrapper}>
                    <input
                      onChange={handleChange}
                      onFocus={() => {
                        setPasswordInputHasFocus(true);
                      }}
                      onBlur={(e) => {
                        setPasswordInputHasFocus(false);
                        handleBlur(e);
                      }}
                      style={styles.passwordInput}
                      placeholder={passwordInputHasFocus ? "" : "Пароль"}
                      type={passwordType}
                      name="password"
                      value={values.password}
                    />
                    {errors.password && touched.password && (
                      <Text weight="regular" style={styles.fieldErrorMessage}>
                        {errors.password}
                      </Text>
                    )}
                    <Text
                      weight="regular"
                      style={getInputStyles(passwordInputHasFocus, styles)}
                    >
                      Пароль
                    </Text>
                    <Text
                      onClick={togglePasswordType}
                      weight="regular"
                      style={styles.showPasswordText}
                    >
                      {passwordType === "password" ? "Показать" : "Скрыть"}
                    </Text>
                  </Div>

                  <Button
                    size="xl"
                    style={styles.submitButton}
                    disabled={isSubmitting}
                    type="submit"
                  >
                    Войти
                  </Button>
                </>
              )}
              <Button
                size="xl"
                style={styles.submitButton}
                onClick={handleOAuthGosuslugi}
              >
                Войти через Госуслуги
              </Button>
              <Text
                weight="regular"
                style={{ textAlign: "center", marginTop: "10px" }}
              >
                <a
                  style={styles.dontKnowPassword}
                  href="https://esia.gosuslugi.ru/recovery/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Я не знаю пароль
                </a>
              </Text>
            </form>
          )}
        </Formik>
      </Div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    loginFormIsSubmitting: state.loader.showLoginFormLoader,
    accessToken: state.user.accessToken,
    expiresIn: state.user.expiresIn,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      startAuthentication: userAuthenticationStarted,
      startUserInfoLoading: userInfoLoadingStarted,
      dispatchHideLoginFormLoader: hideLoginFormLoader,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
